/**
 * The screen editor's Publish tab, lower half: what this group has already
 * published, each with its history (`docs/plans/group-accounts.md` §5).
 *
 * Drawn by the host and handed to the island as a slot, because History and
 * Restore talk to the admin API and `@qufolio/screener-grid` knows no URL.
 * Publishing is the form above it — the same name replaces for every member —
 * so each row's menu is the history alone.
 */
import { useState } from "react";

import type { Screen } from "../lib/screens";
import { PublishedMenu } from "./PublishedMenu";

export interface PublishedScreensProps {
  /** Every screen this account can see; the group's are picked out here. */
  screens: readonly Screen[];
  /** The group being authored for, by key and as it reads. */
  group: string;
  groupName: string;
  /** Re-read the catalog after a Restore. */
  onChanged: () => Promise<void>;
}

export function PublishedScreens({ screens, group, groupName, onChanged }: PublishedScreensProps) {
  const [open, setOpen] = useState<string | null>(null);
  const published = screens.filter((s) => s.ownerKind === "org" && s.ownerKey === group);
  if (published.length === 0) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <div style={HEADING}>Published to {groupName}</div>
      {published.map((screen) => (
        <div key={screen.key}>
          <button
            type="button"
            aria-expanded={open === screen.key}
            onClick={() => setOpen((was) => (was === screen.key ? null : screen.key))}
            style={ROW}
          >
            <span style={{ flex: 1, minWidth: 0, textAlign: "left" }}>{screen.name}</span>
            {screen.version !== undefined && <span style={MUTED}>v{screen.version}</span>}
            <span style={MUTED}>History {open === screen.key ? "▴" : "▾"}</span>
          </button>
          {open === screen.key && (
            <PublishedMenu
              inline
              kind="presets"
              presetKey={screen.key}
              groupName={groupName}
              {...(screen.version !== undefined ? { current: screen.version } : {})}
              onRestored={onChanged}
              onClose={() => setOpen(null)}
            />
          )}
        </div>
      ))}
    </div>
  );
}

const HEADING: React.CSSProperties = {
  paddingTop: 8,
  borderTop: "1px solid var(--qf-border)",
  fontSize: 11,
  textTransform: "uppercase",
  letterSpacing: 0.6,
  color: "var(--qf-text-muted)",
};
const ROW: React.CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  gap: 8,
  width: "100%",
  padding: "5px 2px",
  border: 0,
  background: "none",
  color: "var(--qf-text)",
  font: "inherit",
  fontSize: 12,
  cursor: "pointer",
};
const MUTED: React.CSSProperties = { color: "var(--qf-text-muted)", whiteSpace: "nowrap" };
